/* i18n — minimal, zero-dependency translation layer for LearnixTa.
   Three languages: en (English), te (తెలుగు), hi (हिन्दी).
   Resource files: /src/i18n/{en,te,hi}.json
   API:
     useT()  →  { t, lang, setLang, langs }
     t(key)  →  resolves dotted path; falls back to en, then echoes key
     setLang('te') → persists to localStorage + updates ?lang= URL + dispatches 'lxt:langchange'

   URL support:
     - Visiting `/anything?lang=te` automatically switches to Telugu (and persists).
     - Useful for shareable trilingual links in marketing campaigns.
*/
import { useEffect, useState } from 'react';
import en from './en.json';
import te from './te.json';
import hi from './hi.json';

const DICTS = { en, te, hi };
const LS_KEY = 'lxt_lang';

const _readUrlLang = () => {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const candidate = params.get('lang');
    if (candidate && DICTS[candidate]) return candidate;
  } catch { /* ignore */ }
  return null;
};

const getInitial = () => {
  if (typeof window === 'undefined') return 'en';
  const fromUrl = _readUrlLang();
  if (fromUrl) {
    try { window.localStorage.setItem(LS_KEY, fromUrl); } catch { /* ignore */ }
    return fromUrl;
  }
  const stored = window.localStorage?.getItem(LS_KEY);
  if (stored && DICTS[stored]) return stored;
  const browser = (window.navigator?.language || 'en').slice(0, 2).toLowerCase();
  return DICTS[browser] ? browser : 'en';
};

let _current = getInitial();

const _updateUrlParam = (l) => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (l === 'en') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', l);
    }
    window.history.replaceState({}, '', url.toString());
  } catch { /* ignore */ }
};

const _resolve = (path, dict) => {
  let v = dict;
  for (const p of path.split('.')) v = (v && typeof v === 'object') ? v[p] : undefined;
  return typeof v === 'string' ? v : null;
};

export const getLang = () => _current;

export const setLang = (l) => {
  if (!DICTS[l]) return;
  _current = l;
  try { window.localStorage.setItem(LS_KEY, l); } catch { /* ignore */ }
  _updateUrlParam(l);
  window.dispatchEvent(new Event('lxt:langchange'));
};

export const t = (key, vars) => {
  let s = _resolve(key, DICTS[_current]) || _resolve(key, DICTS.en) || key;
  if (vars && typeof s === 'string') {
    for (const k of Object.keys(vars)) {
      s = s.split(`{${k}}`).join(String(vars[k]));
    }
  }
  return s;
};

export const useT = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener('lxt:langchange', h);
    // Listen for browser back/forward — re-read ?lang
    const onPop = () => {
      const fromUrl = _readUrlLang();
      if (fromUrl && fromUrl !== _current) {
        _current = fromUrl;
        try { window.localStorage.setItem(LS_KEY, fromUrl); } catch { /* ignore */ }
        window.dispatchEvent(new Event('lxt:langchange'));
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('lxt:langchange', h);
      window.removeEventListener('popstate', onPop);
    };
  }, []);
  return {
    t,
    lang: _current,
    setLang,
    langs: [
      { code: 'en', label: 'EN', full: 'English' },
      { code: 'te', label: 'తె', full: 'తెలుగు' },
      { code: 'hi', label: 'हि', full: 'हिन्दी' },
    ],
  };
};
